import { useLocation, Link } from 'react-router-dom';
import { CaretRight, List } from '@phosphor-icons/react';
import { useTraining } from './TrainingContext';

/* ── Breadcrumb segment map ── */

const SEGMENT_LABELS: Record<string, string> = {
  training: 'Training',
  catalog: 'Browse Events',
  event: 'Event',
  detail: 'Details',
  portal: 'My Registrations',
  registration: 'Course Progress',
  cancel: 'Cancel Registration',
  share: 'Shared Link',
};

function buildBreadcrumbs(pathname: string): Array<{ label: string; path: string | null }> {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Array<{ label: string; path: string | null }> = [];

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (/^\d+$/.test(seg) || seg.length > 20) continue;
    const label = SEGMENT_LABELS[seg] || seg.charAt(0).toUpperCase() + seg.slice(1);
    const path = i < segments.length - 1 ? '/' + segments.slice(0, i + 1).join('/') : null;
    crumbs.push({ label, path });
  }

  return crumbs;
}

/* ── Topbar ── */

export default function TrainingTopbarNew() {
  const location = useLocation();
  const {
    pageTitle, learner, org,
    setMobileSidebarOpen,
  } = useTraining();

  const breadcrumbs = buildBreadcrumbs(location.pathname);
  const learnerInitials = learner
    ? `${(learner.first_name || '')[0] || ''}${(learner.last_name || '')[0] || ''}`.toUpperCase()
    : null;

  return (
    <header
      className="sticky top-0 z-[70] bg-white border-b border-gray-200"
      style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
    >
      <div className="flex items-center justify-between h-16 px-6">
        {/* ── Left: mobile trigger + breadcrumbs + title ── */}
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="lg:hidden flex-shrink-0 w-9 h-9 rounded-md bg-transparent border border-gray-200 text-gray-500 hover:bg-gray-50 flex items-center justify-center cursor-pointer transition-colors"
            aria-label="Open menu"
          >
            <List size={18} weight="bold" />
          </button>

          <div className="min-w-0">
            {/* Breadcrumbs */}
            {breadcrumbs.length > 1 && (
              <nav className="flex items-center gap-1.5 mb-0.5">
                {breadcrumbs.map((crumb, i) => (
                  <span key={i} className="flex items-center gap-1.5">
                    {i > 0 && <CaretRight size={10} weight="bold" className="text-gray-300" />}
                    {crumb.path ? (
                      <Link to={crumb.path} className="text-xs text-gray-500 hover:text-gray-900 transition-colors no-underline">
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="text-xs text-gray-900 font-medium">{crumb.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            )}
            {/* Page title */}
            <h1 className="text-sm font-semibold text-gray-900 leading-tight truncate m-0">
              {pageTitle}
            </h1>
          </div>
        </div>

        {/* ── Right: org badge + learner avatar ── */}
        <div className="flex items-center gap-6 flex-shrink-0">
          {/* Org badge */}
          {org && (
            <div className="hidden sm:flex items-center gap-2">
              {org.logo ? (
                <img src={org.logo} alt="" className="w-5 h-5 object-contain rounded" />
              ) : (
                <div className="w-5 h-5 rounded bg-[#4B45E5] text-white flex items-center justify-center text-[10px] font-semibold">
                  {org.company_name?.charAt(0) || 'T'}
                </div>
              )}
              <span className="text-sm text-gray-500 max-w-[140px] truncate">
                {org.company_name}
              </span>
            </div>
          )}

          {/* Learner avatar */}
          {learnerInitials && learner && (
            <div className="relative group">
              <button className="flex items-center gap-2 p-1.5 pr-2 rounded-full border border-transparent bg-transparent hover:bg-gray-50 hover:border-gray-200 cursor-pointer transition-colors">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-medium bg-[#B6B7E0] text-black">
                  {learnerInitials}
                </div>
              </button>
              {/* Hover dropdown */}
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)] border border-gray-100 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-100 z-[80]">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {learner.first_name} {learner.last_name}
                  </div>
                  <div className="text-xs text-gray-500 truncate mt-0.5">
                    {learner.email}
                  </div>
                </div>
                {learner.organization_name && (
                  <div className="px-4 py-2.5 text-xs text-gray-500">
                    {learner.organization_name}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
